import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, Filter } from 'lucide-react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import SearchBar from '../../../components/ui/SearchBar';
import AnchorDropdownPanel from '../../../components/ui/AnchorDropdownPanel';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { dropdown, table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';
import SelectDropdown from '../../../components/ui/SelectDropdown';

/** Default: newest PR first (by `createdAt`). */
const DEFAULT_PR_SORT = [{ id: 'pr', desc: true }];

const PR_SORT_OPTIONS = [
  { key: 'pr-desc', columnId: 'pr', desc: true, label: 'Date (newest first)' },
  { key: 'pr-asc', columnId: 'pr', desc: false, label: 'Date (oldest first)' },
  { key: 'pr-files-desc', columnId: 'prFiles', desc: true, label: 'Files changed (high → low)' },
  { key: 'pr-files-asc', columnId: 'prFiles', desc: false, label: 'Files changed (low → high)' },
  { key: 'pr-churn-desc', columnId: 'prChurn', desc: true, label: 'Churn ratio (high → low)' },
  { key: 'pr-churn-asc', columnId: 'prChurn', desc: false, label: 'Churn ratio (low → high)' },
  { key: 'pr-add-desc', columnId: 'prAdditions', desc: true, label: 'Additions (high → low)' },
  { key: 'pr-add-asc', columnId: 'prAdditions', desc: false, label: 'Additions (low → high)' },
  { key: 'pr-del-desc', columnId: 'prDeletions', desc: true, label: 'Deletions (high → low)' },
  { key: 'pr-del-asc', columnId: 'prDeletions', desc: false, label: 'Deletions (low → high)' },
];

export default function RepositoryPullRequestsTab({
  repositoryId,
  pullRequests,
  pullRequestsStats,
  pullRequestsLoading,
  pullRequestsColumns,
}: any) {
  const [prSearch, setPrSearch] = useState('');
  const [prSort, setPrSort] = useState(DEFAULT_PR_SORT);
  const [prColumnFilters, setPrColumnFilters] = useState<any[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [draftPrState, setDraftPrState] = useState('all');
  const filterAnchorRef = useRef<any>(null);
  const sortAnchorRef = useRef<any>(null);

  useEffect(() => {
    setPrSearch('');
    setPrSort(DEFAULT_PR_SORT);
    setPrColumnFilters([]);
  }, [repositoryId]);

  useEffect(() => {
    if (!filterOpen) return;
    const cur = prColumnFilters.find((f) => f.id === 'prState')?.value ?? 'all';
    setDraftPrState(typeof cur === 'string' ? cur : 'all');
  }, [filterOpen, prColumnFilters]);

  const hasActivePrFilters = Boolean(
    prColumnFilters.some((f) => f.id === 'prState' && f.value && String(f.value) !== 'all')
  );

  const handleApplyFilters = () => {
    setPrColumnFilters((prev) => {
      const rest = prev.filter((f) => f.id !== 'prState');
      if (!draftPrState || draftPrState === 'all') return rest;
      return [...rest, { id: 'prState', value: draftPrState }];
    });
    setFilterOpen(false);
  };

  const handleClearAppliedFilters = () => {
    setPrColumnFilters((prev) => prev.filter((f) => f.id !== 'prState'));
    setDraftPrState('all');
    setFilterOpen(false);
  };

  return (
    <div role="tabpanel" id="repo-tabpanel-pull-requests" aria-labelledby="repo-tab-pull-requests">
      <FeatureGate flag="ui.repositoryDetail.pullRequestsTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <SectionSubtitle>Pull requests</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                {pullRequestsStats != null && (
                  <>
                    Open: {pullRequestsStats.open ?? 0} · Closed: {pullRequestsStats.closed ?? 0}
                    {pullRequestsStats.merged != null && <> · Merged: {pullRequestsStats.merged}</>}
                    <span className="text-slate-600" aria-hidden>
                      {' '}
                      ·{' '}
                    </span>
                  </>
                )}
                Showing up to {pullRequests.length} loaded. Search and sort apply to loaded rows;
                state filter applies on this page.
              </p>
            </div>
            {!pullRequestsLoading && (
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:max-w-xl md:max-w-2xl">
                <div className="min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md">
                  <SearchBar
                    value={prSearch}
                    onChange={setPrSearch}
                    placeholder="Search pull requests…"
                  />
                </div>
                <button
                  ref={sortAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setSortOpen((v) => !v)}
                  aria-label="Sort pull requests"
                  title="Sort pull requests"
                  aria-expanded={sortOpen}
                  aria-haspopup="dialog"
                  aria-controls="repo-pr-sort-menu"
                >
                  <ArrowUpDown className="h-5 w-5 text-slate-200" aria-hidden />
                </button>
                <button
                  ref={filterAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-label="Filter pull requests by state"
                  title="Filter pull requests"
                  aria-expanded={filterOpen}
                  aria-haspopup="dialog"
                  aria-controls="repo-pr-filter-menu"
                >
                  <span className="relative inline-flex">
                    <Filter className="h-5 w-5 text-slate-200" aria-hidden />
                    {hasActivePrFilters && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vivid-red ring-2 ring-dark-surface"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>
          <FilterableDataTable
            key={`pull-requests-${repositoryId}`}
            data={pullRequests}
            columns={pullRequestsColumns}
            getRowId={(row) => String(row.id)}
            isLoading={pullRequestsLoading}
            emptyMessage="No pull requests found."
            searchPlaceholder="Search pull requests…"
            initialSorting={DEFAULT_PR_SORT}
            sorting={prSort}
            onSortingChange={setPrSort}
            showColumnHeaders={false}
            globalFilter={prSearch}
            onGlobalFilterChange={setPrSearch}
            hideTopSearchRow
            columnFilters={prColumnFilters}
            onColumnFiltersChange={setPrColumnFilters}
          />

          <AnchorDropdownPanel
            open={sortOpen}
            onClose={() => setSortOpen(false)}
            anchorRef={sortAnchorRef}
            id="repo-pr-sort-menu"
            ariaLabel="Sort pull requests"
            panelWidth={300}
          >
            <div className="space-y-1" role="list">
              {PR_SORT_OPTIONS.map((opt) => {
                const active =
                  prSort.length === 1 &&
                  prSort[0].id === opt.columnId &&
                  prSort[0].desc === opt.desc;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="listitem"
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                      active ? 'bg-brand-purple/15 text-brand-purple-light' : dropdown.item
                    )}
                    onClick={() => {
                      setPrSort([{ id: opt.columnId, desc: opt.desc }]);
                      setSortOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AnchorDropdownPanel>

          <AnchorDropdownPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterAnchorRef}
            id="repo-pr-filter-menu"
            title="Filter pull requests"
            titleIcon={<Filter className="h-5 w-5 shrink-0 text-vivid-red" aria-hidden />}
            panelWidth={320}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="pr-state-filter"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  State
                </label>
                <SelectDropdown
                  id="pr-state-filter"
                  className="w-full"
                  value={draftPrState}
                  onChange={setDraftPrState}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'open', label: 'Open' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                  compact
                />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={handleClearAppliedFilters}
                  disabled={!hasActivePrFilters && (!draftPrState || draftPrState === 'all')}
                >
                  Clear filters
                </button>
                <button type="button" className="btn-primary-mdl-sm" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </AnchorDropdownPanel>
        </div>
      </FeatureGate>
    </div>
  );
}
